import './theme.scss';
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalConfig, loginApiRequest } from "./authconfig";
import { BrowserRouter as Router } from 'react-router-dom';
import axios from "axios";

export const msalInstance = new PublicClientApplication(msalConfig);

const baseUrl = document.getElementsByTagName('base')[0].getAttribute('href');
const rootElement = document.getElementById('root');
const root = createRoot(rootElement);

// Clean up the bearer token an older version of the app copied into localStorage
localStorage.removeItem("UniversalDesignAuth");

// Guard so a burst of parallel requests triggers at most one interactive redirect
let redirectingForAuth = false;

const redirectForInteraction = (account) => {
    if (redirectingForAuth) return;
    redirectingForAuth = true;
    if (account)
        msalInstance.acquireTokenRedirect({ ...loginApiRequest, account: account });
    else
        msalInstance.loginRedirect(loginApiRequest);
};

axios.interceptors.request.use(
    async (config) => {
        const account = msalInstance.getAllAccounts()[0];

        if (!account) {
            // Session is gone entirely — sending the request would just 401.
            // Kick off an interactive sign-in and fail this request cleanly.
            redirectForInteraction(null);
            return Promise.reject(new axios.Cancel("Signed out — redirecting to sign in"));
        }

        try {
            const msalResponse = await msalInstance.acquireTokenSilent({
                ...loginApiRequest,
                account: account,
            });
            config.headers['Authorization'] = `Bearer ${msalResponse.accessToken}`;
            return config;
        }
        catch (error) {
            // Refresh token expired / Conditional Access wants interaction —
            // silent renewal can't recover, so go interactive instead of letting
            // every request fail behind a generic error toast
            if (error instanceof InteractionRequiredAuthError) {
                redirectForInteraction(account);
                return Promise.reject(new axios.Cancel("Session expired — redirecting to sign in"));
            }
            return Promise.reject(error);
        }
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Backstop: if the API rejects a token the silent flow considered valid
// (e.g. revoked session), recover with an interactive sign-in
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401)
            redirectForInteraction(msalInstance.getAllAccounts()[0] ?? null);
        return Promise.reject(error);
    }
);

root.render(
        <Router basename={baseUrl}>
            <App instance={msalInstance} />
        </Router>
);
